import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import QRCode from 'qrcode';
import QRCodeSVG from 'react-qr-code';
import { jsPDF } from 'jspdf';
import { FaSearch, FaDownload, FaEye, FaTimes, FaBox, FaInfo, FaImage, FaTh, FaTable, FaEdit, FaQrcode, FaFilePdf, FaFilter, FaChevronDown } from 'react-icons/fa';

const StockItemsStatus = () => {
  const { user } = useContext(AuthContext);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(16);
  const [viewMode, setViewMode] = useState('cards');
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const reqSeqRef = useRef(0);

  // Enhanced pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    itemsPerPage: 16,
    startIndex: 0,
    endIndex: 0,
    pageRange: { start: 1, end: 1 }
  });

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalStockItems: 0,
    currentPageItems: 0,
    stockSummary: {
      outOfStock: 0,
      lowStock: 0,
      available: 0
    }
  });

  // Filter states
  const [filters, setFilters] = useState({
    brand: null,
    category: null,
    status: 'active',
    stockFilter: null,
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');



  // Stock editing states
  const [showStockEditModal, setShowStockEditModal] = useState(false);
  const [selectedStockEdit, setSelectedStockEdit] = useState(null);
  const [stockEditLoading, setStockEditLoading] = useState(false);
  const [editingStock, setEditingStock] = useState({
    freshQuantity: 0,
    damagedQuantity: 0
  });

  // Image editing states
  const [showImageEditModal, setShowImageEditModal] = useState(false);
  const [selectedImageEdit, setSelectedImageEdit] = useState(null);
  const [imageEditLoading, setImageEditLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // QR Code states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrls, setQrCodeUrls] = useState({ objectId: '', partNumber: '' });
  const [selectedItemForQR, setSelectedItemForQR] = useState(null);
  const [qrName, setQrName] = useState(null);
  const [activeQRType, setActiveQRType] = useState('objectId');

  // Status change states
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const syncDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(syncDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Add status change function
  const handleStatusChange = async (stockItem) => {
    try {
      setStatusChangeLoading(true);
      const newStatus = stockItem.status === 'active' ? 'inactive' : 'active';
      
      const confirmMessage = `Are you sure you want to ${newStatus === 'inactive' ? 'deactivate' : 'activate'} this stock item?`;
      
      const confirmed = await Swal.fire({
        title: confirmMessage,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      });

      if (!confirmed.isConfirmed) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items/update-status/${stockItem._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const result = await response.json();

      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: result.message,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        fetchStockItems(); // Refresh the stock items list
      } else {
        Swal.fire({
          title: 'Error!',
          text: result.message || 'Failed to update stock item status',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating stock item status:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update stock item status. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Fetch stock items data with server-side pagination
  const fetchStockItems = async (signal) => {
    const seq = ++reqSeqRef.current;
    const isLatest = () => seq === reqSeqRef.current;
    try {
      setError(null);
      const isInitial = !hasLoadedOnceRef.current;
      setLoading(isInitial);
      setIsRefreshing(!isInitial);

      // Build query parameters for API call
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        filter: searchQuery,
        userId: user.user._id,
        status: filters.status,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      // Add brand filter if selected
      if (filters.brand) {
        queryParams.append('brand', filters.brand.value);
      }

      // Add category filter if selected
      if (filters.category) {
        queryParams.append('category', filters.category.value);
      }

      // Add stock filter if selected
      if (filters.stockFilter) {
        queryParams.append('stockFilter', filters.stockFilter.value);
      }

      // Add location if selected
      if (selectedLocation) {
        queryParams.set('location', selectedLocation);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/stock-items/stock-status?${queryParams}`,
        signal ? { signal } : undefined
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!isLatest()) return;

      if (result.success) {
        hasLoadedOnceRef.current = true;
        setStockItems(result.data.stockItems || []);
        setPagination(result.data.pagination || {});
        setSummaryStats(result.data.summary || {});
      } else {
        throw new Error(result.message || 'Failed to fetch stock items');
      }
    } catch (error) {
      if (error?.name === 'AbortError' || !isLatest()) return;
      console.error('Error fetching stock items:', error);
      setError(error.message);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch stock items data. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      if (isLatest()) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  const fetchBrandsAndCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const authOpts = { headers: { 'Authorization': `Bearer ${token}` } };
      const [brandsResponse, categoriesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/brands?userId=${user.user._id}`, authOpts),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/categories?userId=${user.user._id}`, authOpts)
      ]);

      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        setBrands(Array.isArray(brandsData) ? brandsData : brandsData.brands || []);
      }
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(Array.isArray(categoriesData) ? categoriesData : categoriesData.categories || []);
      }
    } catch (error) {
      console.error('Error fetching brands and categories:', error);
    }
  };



  // Stock editing functions
  const handleEditStock = async (stockItem, stockEntry) => {
    try {
      // Check if stock item is used in transactions
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/stock-items/check-usage/${stockItem._id}/${stockEntry.location._id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        if (!result.canEdit) {
          Swal.fire({
            title: 'Cannot Edit Stock',
            text: 'This stock item has been used in transactions and cannot be edited.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
          return;
        }
      }

      // Fetch latest stock values to avoid stale in-memory data overwriting DB
      let freshQty = stockEntry.freshQuantity || 0;
      let damagedQty = stockEntry.damagedQuantity || 0;
      try {
        const latestRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/stock-items/get-stock-item/${stockItem._id}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        if (latestRes.ok) {
          const latestData = await latestRes.json();
          const latestEntry = latestData.stockItem?.stock?.find(
            s => s.location?._id === stockEntry.location._id || s.location === stockEntry.location._id
          );
          if (latestEntry) {
            freshQty = latestEntry.freshQuantity || 0;
            damagedQty = latestEntry.damagedQuantity || 0;
          }
        }
      } catch (_) { /* fall back to in-memory values */ }

      setSelectedStockEdit({ stockItem, stockEntry });
      setEditingStock({ freshQuantity: freshQty, damagedQuantity: damagedQty });
      setShowStockEditModal(true);
    } catch (error) {
      console.error('Error checking stock item usage:', error);
      // Continue with edit even if check fails
      setSelectedStockEdit({ stockItem, stockEntry });
      setEditingStock({
        freshQuantity: stockEntry.freshQuantity || 0,
        damagedQuantity: stockEntry.damagedQuantity || 0
      });
      setShowStockEditModal(true);
    }
  };

  const handleSaveStock = async () => {
    if (!selectedStockEdit) return;

    setStockEditLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/stock-items/edit-stock/${selectedStockEdit.stockItem._id}/${selectedStockEdit.stockEntry.location._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(editingStock)
        }
      );

      const result = await response.json();
      
      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Stock updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setShowStockEditModal(false);
        fetchStockItems(); // Refresh data
      } else {
        Swal.fire({
          title: 'Error!',
          text: result.message || 'Failed to update stock.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error updating stock.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setStockEditLoading(false);
    }
  };

  // Image editing functions
  const handleEditImage = (stockItem) => {
    setSelectedImageEdit(stockItem);
    setSelectedImage(null);
    setShowImageEditModal(true);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: 'File too large',
          text: 'Image size should be less than 5MB.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Invalid file type',
          text: 'Only JPG, JPEG and PNG images are allowed.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      setSelectedImage(file);
    }
  };

  const handleSaveImage = async () => {
    if (!selectedImageEdit || !selectedImage) return;

    setImageEditLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/stock-items/update-image/${selectedImageEdit._id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      const result = await response.json();
      
      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Image updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setShowImageEditModal(false);
        fetchStockItems(); // Refresh data
      } else {
        Swal.fire({
          title: 'Error!',
          text: result.message || 'Failed to update image.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error updating image:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error updating image.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setImageEditLoading(false);
    }
  };

  // Event handlers
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(1); // Reset to first page when filtering
  };

  const resetFilters = () => {
    setFilters({
      brand: null,
      category: null,
      status: 'active',
      stockFilter: null,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    setSearchQuery('');
    setSelectedLocation('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const openDetailsModal = (stockItem) => {
    setSelectedStockItem(stockItem);
    setShowDetailsModal(true);
  };

  const handleGenerateQR = async (stockItem) => {
    try {
      const qrCodes = {};
      
      // Generate QR code for ObjectId (always)
      const objectIdQR = await QRCode.toDataURL(stockItem._id, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      });
      qrCodes.objectId = objectIdQR;
      
      // Generate QR code for Part Number (only if it exists)
      if (stockItem.partNumber && stockItem.partNumber.trim() !== '') {
        const partNumberQR = await QRCode.toDataURL(stockItem.partNumber, {
          width: 300,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' }
        });
        qrCodes.partNumber = partNumberQR;
      }
      
      setQrCodeUrls(qrCodes);
      setSelectedItemForQR(stockItem);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      Swal.fire('Error', 'Failed to generate QR code', 'error');
    }
  };

  const handleDownloadQR = (type) => {
    const link = document.createElement('a');
    const qrUrl = type === 'objectId' ? qrCodeUrls.objectId : qrCodeUrls.partNumber;
    const fileName = type === 'objectId' 
      ? `stockitem-${selectedItemForQR?.name || selectedItemForQR?._id}-objectid-qr.png`
      : `stockitem-${selectedItemForQR?.name || selectedItemForQR?._id}-partno-qr.png`;
    
    link.download = fileName;
    link.href = qrUrl;
    link.click();
  };

  const downloadQRPdf = async (includeQrName = false) => {
    const svgId = activeQRType === 'partNumber' ? 'qr-code-svg-partno' : 'qr-code-svg-objid';
    const svg = document.getElementById(svgId);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngDataUrl = canvas.toDataURL('image/png');
      
      if (includeQrName) {
        downloadQRWithNamePdf(pngDataUrl);
      } else {
        downloadQROnlyPdf(pngDataUrl);
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const downloadQROnlyPdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    pdf.addImage(qrImageData, 'PNG', 5, 15, 35, 35);
    const itemName = selectedItemForQR?.name || 'StockItem';
    const suffix = activeQRType === 'partNumber' ? '_PartNo_QR' : '_QR';
    pdf.save(`${itemName}${suffix}.pdf`);
  };

  const downloadQRWithNamePdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    
    const qrNameText = qrName || 'DOMUS';
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const qrNameWidth = pdf.getTextWidth(qrNameText);
    const qrNameX = (45 - qrNameWidth) / 2;
    const qrNameY = 8;
    pdf.text(qrNameText, qrNameX, qrNameY);
    
    const qrSize = 25;
    const qrX = (45 - qrSize) / 2;
    const qrY = qrNameY + 4;
    pdf.addImage(qrImageData, 'PNG', qrX, qrY, qrSize, qrSize);
    
    const arrowY = qrY + qrSize + 4;
    const arrowX = 45 / 2;
    pdf.setLineWidth(0.5);
    pdf.line(arrowX, arrowY, arrowX, arrowY + 3);
    pdf.line(arrowX - 1, arrowY + 2, arrowX, arrowY + 3);
    pdf.line(arrowX + 1, arrowY + 2, arrowX, arrowY + 3);
    
    const itemNameText = selectedItemForQR?.name || 'StockItem';
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    const maxWidth = 38;
    const lines = pdf.splitTextToSize(itemNameText, maxWidth);
    const lineHeight = 3.0;
    const startY = arrowY + 8;
    
    lines.forEach((line, index) => {
      const lineWidth = pdf.getTextWidth(line);
      const lineX = (45 - lineWidth) / 2;
      const lineY = startY + (index * lineHeight);
      pdf.text(line, lineX, lineY);
    });
    
    const suffix = activeQRType === 'partNumber' ? '_PartNo_QR_with_name' : '_QR_with_name';
    pdf.save(`${selectedItemForQR?.name || 'StockItem'}${suffix}.pdf`);
  };

  // Effects
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      fetchStockItems(controller.signal);
    }, 500); // Debounce search and filters

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery, page, limit, filters, selectedLocation]);

  // Initialize data fetching
  useEffect(() => {
    fetchBrandsAndCategories();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`);
      const data = await response.json();
      setLocations(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Calculate stock status for a stock item
  const getStockStatus = (stockItem) => {
    const totalStock = stockItem.stock?.reduce((sum, s) => sum + (s.freshQuantity || 0), 0) || 0;
    if (totalStock === 0) return { status: 'out', color: 'red', label: 'Out of Stock' };
    if (totalStock < 10) return { status: 'low', color: 'yellow', label: 'Low Stock' };
    return { status: 'available', color: 'green', label: 'Available' };
  };

  // Enhanced pagination component
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const getPageNumbers = () => {
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (let i = Math.max(2, page - delta); i <= Math.min(pagination.totalPages - 1, page + delta); i++) {
        range.push(i);
      }

      if (page - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (page + delta < pagination.totalPages - 1) {
        rangeWithDots.push('...', pagination.totalPages);
      } else if (pagination.totalPages > 1) {
        rangeWithDots.push(pagination.totalPages);
      }

      return rangeWithDots;
    };

    return (
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Results info */}
          <div className="flex items-center gap-4">
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing <span className="font-medium">{((page - 1) * limit) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * limit, pagination.totalItems)}</span> of{' '}
              <span className="font-medium">{pagination.totalItems}</span> results
            </p>
            
            {/* Items per page selector */}
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setPage(1);
              }}
              className={`px-3 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
            >
              <option value={8}>8 per page</option>
              <option value={16}>16 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
            </select>
          </div>
          
          {/* Pagination controls */}
          <nav className="flex items-center space-x-1">
            {/* First page */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
              className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'}`}
              title="First page"
            >
              ««
            </button>
            
            {/* Previous page */}
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={!pagination.hasPrevPage}
              className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'}`}
            >
              Previous
            </button>
            
            {/* Page numbers */}
            {getPageNumbers().map((pageNum, index) => (
              pageNum === '...' ? (
                <span key={index} className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => handlePageChange(pageNum)}
                  className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md transition-colors ${
                    pageNum === page
                      ? darkMode ? 'z-10 bg-blue-900/30 border-blue-500 text-blue-400' : 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              )
            ))}
            
            {/* Next page */}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={!pagination.hasNextPage}
              className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'}`}
            >
              Next
            </button>
            
            {/* Last page */}
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={page === pagination.totalPages}
              className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'}`}
              title="Last page"
            >
              »»
            </button>
          </nav>
        </div>
        
        {/* Quick page jump */}
        <div className={`mt-4 flex items-center justify-center gap-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <span>Jump to page:</span>
          <input
            type="number"
            min={1}
            max={pagination.totalPages}
            value={page}
            onChange={(e) => {
              const newPage = parseInt(e.target.value);
              if (newPage >= 1 && newPage <= pagination.totalPages) {
                handlePageChange(newPage);
              }
            }}
            className={`w-16 px-2 py-1 border rounded text-center focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
          />
          <span>of {pagination.totalPages}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen py-6 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className={`rounded-xl shadow-lg border-2 mb-6 overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-700' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Stock Items Status</h1>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Monitor and manage stock items inventory across all locations
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Summary Cards */}
                <div className="flex gap-2">
                  <div className={`px-3 py-2 rounded-lg text-sm font-medium shadow-sm ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                    <span className="font-bold">{summaryStats.stockSummary.available}</span> Available
                  </div>
                  <div className={`px-3 py-2 rounded-lg text-sm font-medium shadow-sm ${darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-700'}`}>
                    <span className="font-bold">{summaryStats.stockSummary.lowStock}</span> Low Stock
                  </div>
                  <div className={`px-3 py-2 rounded-lg text-sm font-medium shadow-sm ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                    <span className="font-bold">{summaryStats.stockSummary.outOfStock}</span> Out of Stock
                  </div>
                </div>


              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    placeholder="Search by name, part#, brand, category..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => handleSearch({ target: { value: '' } })}
                      title="Clear search"
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors ${
                        darkMode
                          ? 'text-gray-400 hover:text-white hover:bg-gray-600'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Brand Filter - React Select */}
              <div>
                <Select
                  isClearable
                  options={brands.map(b => ({ value: b._id, label: b.name }))}
                  value={filters.brand}
                  onChange={(option) => handleFilterChange('brand', option)}
                  placeholder="Select brand..."
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff',
                      borderColor: darkMode ? '#4B5563' : '#d1d5db',
                      borderWidth: '1px',
                      color: darkMode ? '#e5e7eb' : '#111827'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#3b82f6' : (darkMode ? '#374151' : '#ffffff'),
                      color: state.isSelected ? '#ffffff' : (darkMode ? '#e5e7eb' : '#111827'),
                      ':hover': { backgroundColor: '#60a5fa', color: '#ffffff' }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: darkMode ? '#e5e7eb' : '#111827'
                    })
                  }}
                />
              </div>

              {/* Category Filter - React Select */}
              <div>
                <Select
                  isClearable
                  options={categories.map(c => ({ value: c._id, label: c.name }))}
                  value={filters.category}
                  onChange={(option) => handleFilterChange('category', option)}
                  placeholder="Select category..."
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff',
                      borderColor: darkMode ? '#4B5563' : '#d1d5db',
                      borderWidth: '1px',
                      color: darkMode ? '#e5e7eb' : '#111827'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#3b82f6' : (darkMode ? '#374151' : '#ffffff'),
                      color: state.isSelected ? '#ffffff' : (darkMode ? '#e5e7eb' : '#111827'),
                      ':hover': { backgroundColor: '#60a5fa', color: '#ffffff' }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: darkMode ? '#e5e7eb' : '#111827'
                    })
                  }}
                />
              </div>

              {/* Location Filter - React Select */}
              <div>
                <Select
                  isClearable
                  options={locations.map(l => ({ value: l._id, label: l.name || l.locationName }))}
                  value={selectedLocation ? { value: selectedLocation, label: locations.find(l => l._id === selectedLocation)?.name || 'Select...' } : null}
                  onChange={(option) => { setSelectedLocation(option ? option.value : ''); setPage(1); }}
                  placeholder="Select location..."
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff',
                      borderColor: darkMode ? '#4B5563' : '#d1d5db',
                      borderWidth: '1px',
                      color: darkMode ? '#e5e7eb' : '#111827'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#3b82f6' : (darkMode ? '#374151' : '#ffffff'),
                      color: state.isSelected ? '#ffffff' : (darkMode ? '#e5e7eb' : '#111827'),
                      ':hover': { backgroundColor: '#60a5fa', color: '#ffffff' }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: darkMode ? '#e5e7eb' : '#111827'
                    })
                  }}
                />
              </div>

              {/* Stock Filter - React Select */}
              <div>
                <Select
                  isClearable
                  options={[
                    { value: 'available', label: '✓ Available' },
                    { value: 'low', label: '⚠ Low Stock' },
                    { value: 'out', label: '✗ Out of Stock' }
                  ]}
                  value={filters.stockFilter}
                  onChange={(option) => handleFilterChange('stockFilter', option)}
                  placeholder="Select status..."
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff',
                      borderColor: darkMode ? '#4B5563' : '#d1d5db',
                      borderWidth: '1px',
                      color: darkMode ? '#e5e7eb' : '#111827'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#ffffff'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#3b82f6' : (darkMode ? '#374151' : '#ffffff'),
                      color: state.isSelected ? '#ffffff' : (darkMode ? '#e5e7eb' : '#111827'),
                      ':hover': { backgroundColor: '#60a5fa', color: '#ffffff' }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: darkMode ? '#e5e7eb' : '#111827'
                    })
                  }}
                />
              </div>

              {/* Reset Filters */}
              <div>
                <button
                  onClick={resetFilters}
                  className={`w-full px-4 py-2 border rounded-lg transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Reset Filters
                </button>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className={`flex items-center justify-between mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>View:</span>
                <div className={`flex rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`px-3 py-1 text-sm transition-colors ${viewMode === 'cards' ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    <FaTh className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-3 py-1 text-sm transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    <FaTable className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sort by:</span>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className={`px-3 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="name">Name</option>
                  <option value="brand">Brand</option>
                  <option value="createdAt">Date Added</option>
                  <option value="totalStock">Stock Level</option>
                </select>
                <button
                  onClick={() => handleFilterChange('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')}
                  className={`px-2 py-1 border rounded text-sm transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  {filters.sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={`rounded-xl shadow-lg border-2 p-12 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading stock items data...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={`rounded-xl shadow-lg border-2 p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-red-700' : 'bg-white border-red-200'}`}>
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                <FaTimes className={`w-6 h-6 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Error Loading Data</h3>
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{error}</p>
              <button
                onClick={() => fetchStockItems()}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Stock Items Grid/Table */}
        {!loading && !error && (
          <>
            {isRefreshing && (
              <div className={`flex items-center justify-center gap-2 rounded-xl shadow-lg border-2 px-4 py-2 mb-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Updating results...</p>
              </div>
            )}
            {viewMode === 'cards' ? (
              /* Cards View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-6">
                {stockItems.map((stockItem) => {
                  const stockStatus = getStockStatus(stockItem);
                  const totalStock = stockItem.stock?.reduce((sum, s) => sum + (s.freshQuantity || 0), 0) || 0;
                  const totalDamaged = stockItem.stock?.reduce((sum, s) => sum + (s.damagedQuantity || 0), 0) || 0;
                  
                  return (
                    <div key={stockItem._id} className={`rounded-xl shadow-lg border-2 overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-300 ${
                      darkMode ? 'bg-gray-800 border-gray-700 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-400'
                    }`}>
                      <div className="relative aspect-w-16 aspect-h-9 bg-gradient-to-br from-gray-200 to-gray-300">
                        {stockItem.image ? (
                          <>
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}/stock-items/image/${stockItem._id}`}
                              alt={stockItem.name}
                              className="w-full h-48 object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className={`w-full h-48 flex flex-col items-center justify-center ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`} style={{ display: 'none' }}>
                              <FaImage className="w-10 h-10 mb-2" />
                              <span className="text-sm font-medium">No image</span>
                            </div>
                          </>
                        ) : (
                          <div className={`w-full h-48 flex flex-col items-center justify-center ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            <FaImage className="w-10 h-10 mb-2" />
                            <span className="text-sm font-medium">No image</span>
                          </div>
                        )}
                        <div className="absolute top-3 right-3">
                          <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-lg backdrop-blur-sm ${
                            stockStatus.status === 'available' ? 'bg-green-500 text-white' :
                            stockStatus.status === 'low' ? 'bg-yellow-500 text-white' :
                            'bg-red-500 text-white'
                          }`}>
                            {stockStatus.label}
                          </span>
                        </div>
                      </div>

                      <div className="p-5">
                        <h3 className={`font-bold text-base mb-3 line-clamp-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          {stockItem.name}
                        </h3>

                        <div className={`space-y-2 text-xs mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">ID:</span>
                            <span className={`font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stockItem.id}</span>
                          </div>
                          {stockItem.partNumber && (
                            <div className="flex items-center justify-between">
                              <span className="font-medium">Part#:</span>
                              <span className={`font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{stockItem.partNumber}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Brand:</span>
                            <span className="font-semibold">{stockItem.brand?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Category:</span>
                            <span className="font-semibold truncate ml-2">{stockItem.category?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Unit:</span>
                            <span className="font-semibold">{stockItem.unitOfMeasure || 'N/A'}</span>
                          </div>
                        </div>

                        <div className={`rounded-lg p-3 mb-3 ${darkMode ? 'bg-gradient-to-br from-gray-700 to-gray-600' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                          <div className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            📦 Total Stock
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-lg font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                                {totalStock}
                              </span>
                              <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh</span>
                            </div>
                            <div className={`text-right ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <div className={`text-sm font-semibold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{totalDamaged} Damaged</div>
                            </div>
                          </div>
                          {(stockItem.salesOrder?.freshQuantity > 0 || stockItem.salesOrder?.damagedQuantity > 0) && (
                            <div className={`flex items-center justify-between mt-2 pt-2 border-t ${darkMode ? 'border-gray-600' : 'border-blue-200'}`}>
                              <span className={`text-xs font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                🛒 Sales Ordered
                              </span>
                              <span className={`text-xs font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                {stockItem.salesOrder?.freshQuantity > 0 ? `${parseFloat(stockItem.salesOrder.freshQuantity.toFixed(4))} Fresh` : ''}{stockItem.salesOrder?.freshQuantity > 0 && stockItem.salesOrder?.damagedQuantity > 0 ? ', ' : ''}{stockItem.salesOrder?.damagedQuantity > 0 ? `${parseFloat(stockItem.salesOrder.damagedQuantity.toFixed(4))} Dmg` : ''}
                              </span>
                            </div>
                          )}
                          {(stockItem.quantityOrdered > 0 || stockItem.inTransitQuantity > 0) && (
                            <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-gray-600' : 'border-blue-200'} space-y-1`}>
                              {stockItem.quantityOrdered > 0 && (
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                    📋 Ordered
                                  </span>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                                    {stockItem.quantityOrdered}
                                  </span>
                                </div>
                              )}
                              {stockItem.inTransitQuantity > 0 && (
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs font-semibold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                    🚚 In Transit
                                  </span>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${darkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                                    {stockItem.inTransitQuantity}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => openDetailsModal(stockItem)}
                            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                          >
                            <FaEye className="w-4 h-4" />
                            <span>Details</span>
                          </button>
                          <button
                            onClick={() => handleGenerateQR(stockItem)}
                            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg"
                            title="Generate QR Code"
                          >
                            <FaQrcode className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditImage(stockItem)}
                            className={`text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg ${
                              darkMode 
                                ? 'bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500' 
                                : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
                            }`}
                          >
                            <FaImage className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Table View */
              <div className={`rounded-xl shadow-lg border-2 overflow-hidden mb-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={`${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
                      <tr>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Stock Item</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Details</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Stock Status</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Total Stock</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Ordered</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>In Transit</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Net Stock</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Status</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                      {stockItems.map((stockItem) => {
                        const stockStatus = getStockStatus(stockItem);
                        const totalStock = stockItem.stock?.reduce((sum, s) => sum + (s.freshQuantity || 0), 0) || 0;
                        const totalDamaged = stockItem.stock?.reduce((sum, s) => sum + (s.damagedQuantity || 0), 0) || 0;
                        
                        return (
                          <tr key={stockItem._id} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50'} transition-colors duration-150`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-14 w-14 flex-shrink-0 relative">
                                  {stockItem.image ? (
                                    <>
                                      <img
                                        src={`${process.env.REACT_APP_API_BASE_URL}/stock-items/image/${stockItem._id}`}
                                        alt={stockItem.name}
                                        className="h-14 w-14 rounded-xl object-cover shadow-md"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                      <div className={`h-14 w-14 rounded-xl flex flex-col items-center justify-center shadow-md ${
                                        darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
                                      }`} style={{ display: 'none' }}>
                                        <FaImage className="w-4 h-4" />
                                      </div>
                                    </>
                                  ) : (
                                    <div className={`h-14 w-14 rounded-xl flex flex-col items-center justify-center shadow-md ${
                                      darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
                                    }`}>
                                      <FaImage className="w-4 h-4" />
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className={`text-sm font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stockItem.name}</div>
                                  <div className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>ID: {stockItem.id}</div>
                                  {stockItem.partNumber && (
                                    <div className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Part#: {stockItem.partNumber}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <div className="space-y-1">
                                <div><span className="font-medium">Brand:</span> {stockItem.brand?.name || 'N/A'}</div>
                                <div><span className="font-medium">Category:</span> {stockItem.category?.name || 'N/A'}</div>
                                <div><span className="font-medium">Unit:</span> {stockItem.unitOfMeasure || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                                stockStatus.status === 'available' ? 'bg-green-500 text-white' :
                                stockStatus.status === 'low' ? 'bg-yellow-500 text-white' :
                                'bg-red-500 text-white'
                              }`}>
                                {stockStatus.label}
                              </span>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              <div className="font-bold">{totalStock} Fresh</div>
                              <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-500'}`}>{totalDamaged} Damaged</div>
                              {(stockItem.salesOrder?.freshQuantity > 0 || stockItem.salesOrder?.damagedQuantity > 0) && (
                                <div className={`text-xs font-semibold mt-1 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                  🛒 SO: {stockItem.salesOrder?.freshQuantity || 0} Fresh{stockItem.salesOrder?.damagedQuantity > 0 ? `, ${stockItem.salesOrder.damagedQuantity} Dmg` : ''}
                                </div>
                              )}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {stockItem.quantityOrdered > 0 ? (
                                <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                                  {stockItem.quantityOrdered}
                                </span>
                              ) : (
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                              )}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {stockItem.inTransitQuantity > 0 ? (
                                <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {stockItem.inTransitQuantity}
                                </span>
                              ) : (
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                              )}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {(() => {
                                const soFresh = stockItem.salesOrder?.freshQuantity || 0;
                                const soDamaged = stockItem.salesOrder?.damagedQuantity || 0;
                                const netFresh = totalStock - soFresh;
                                const netDamaged = totalDamaged - soDamaged;
                                return (
                                  <>
                                    <div className={`font-bold ${netFresh < 0 ? 'text-red-500' : darkMode ? 'text-green-400' : 'text-green-700'}`}>{netFresh} Fresh</div>
                                    {netDamaged !== 0 && (
                                      <div className={`text-xs ${netDamaged < 0 ? 'text-red-500' : darkMode ? 'text-red-400' : 'text-red-500'}`}>{netDamaged} Damaged</div>
                                    )}
                                  </>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {stockItem.status}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => openDetailsModal(stockItem)}
                                  className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-md hover:shadow-lg"
                                  title="View Details"
                                >
                                  <FaEye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleGenerateQR(stockItem)}
                                  className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-all shadow-md hover:shadow-lg"
                                  title="Generate QR Code"
                                >
                                  <FaQrcode className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditImage(stockItem)}
                                  className={`p-2 rounded-lg transition-all shadow-md hover:shadow-lg ${
                                    darkMode 
                                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                                  }`}
                                  title="Edit Image"
                                >
                                  <FaImage className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {renderPagination()}
          </>
        )}

        {/* No Results */}
        {!loading && !error && !isRefreshing && stockItems.length === 0 && (
          <div className={`rounded-xl shadow-lg border-2 p-12 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="text-center">
              <FaBox className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No stock items found</h3>
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {searchQuery || Object.values(filters).some(f => f) 
                  ? 'Try adjusting your search or filters' 
                  : 'No stock items have been added yet'}
              </p>
              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={resetFilters}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && selectedStockItem && (
        <StockItemDetailsModal
          stockItem={selectedStockItem}
          onClose={() => setShowDetailsModal(false)}
          onEditStock={handleEditStock}
          onEditImage={handleEditImage}
          onStatusChange={handleStatusChange}
          statusChangeLoading={statusChangeLoading}
          darkMode={darkMode}
        />
      )}

      {/* Stock Edit Modal */}
      {showStockEditModal && selectedStockEdit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl max-w-md w-full shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Edit Stock</h3>
                <button
                  onClick={() => setShowStockEditModal(false)}
                  className={`p-2 rounded-xl transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fresh Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={editingStock.freshQuantity}
                    onChange={(e) => setEditingStock(prev => ({ ...prev, freshQuantity: parseFloat(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Damaged Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={editingStock.damagedQuantity}
                    onChange={(e) => setEditingStock(prev => ({ ...prev, damagedQuantity: parseFloat(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowStockEditModal(false)}
                  className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStock}
                  disabled={stockEditLoading}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                >
                  {stockEditLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Edit Modal */}
      {showImageEditModal && selectedImageEdit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl max-w-md w-full shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Update Image</h3>
                <button
                  onClick={() => setShowImageEditModal(false)}
                  className={`p-2 rounded-xl transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Select New Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max size: 5MB. Formats: JPG, JPEG, PNG</p>
                </div>

                {selectedImage && (
                  <div className="mt-4">
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Preview:</p>
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="Preview"
                      className={`w-full h-32 object-cover rounded-lg border ${darkMode ? 'border-gray-600' : ''}`}
                    />
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowImageEditModal(false)}
                  className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveImage}
                  disabled={imageEditLoading || !selectedImage}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                >
                  {imageEditLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedItemForQR && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-2xl max-w-2xl w-full max-h-[90vh] my-4 shadow-2xl flex flex-col ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                QR Codes for {selectedItemForQR.name}
              </h3>
              <button
                onClick={() => setShowQRModal(false)}
                className={`p-2 rounded-xl transition-colors flex-shrink-0 ${
                  darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Hidden SVG QR codes for PDF generation */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <QRCodeSVG id="qr-code-svg-objid" value={selectedItemForQR._id} size={200} bgColor="#ffffff" fgColor="#000000" />
              {selectedItemForQR.partNumber && (
                <QRCodeSVG id="qr-code-svg-partno" value={selectedItemForQR.partNumber} size={200} bgColor="#ffffff" fgColor="#000000" />
              )}
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 flex-1">
              {/* ObjectId QR Code */}
              <div className={`p-4 sm:p-6 rounded-xl border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <h4 className={`text-sm sm:text-md font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Object ID QR Code
                </h4>
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white p-3 sm:p-4 rounded-lg">
                    <img src={qrCodeUrls.objectId} alt="ObjectId QR Code" className="w-48 h-48 sm:w-64 sm:h-64" />
                  </div>
                  <p className={`text-xs sm:text-sm text-center break-all px-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    ID: {selectedItemForQR._id}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <button
                      onClick={() => handleDownloadQR('objectId')}
                      className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaDownload className="w-4 h-4" /> 
                      <span>PNG</span>
                    </button>
                    <button
                      onClick={() => { setActiveQRType('objectId'); setTimeout(() => downloadQRPdf(false), 100); }}
                      className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaFilePdf className="w-4 h-4" />
                      <span>PDF</span>
                    </button>
                    <button
                      onClick={() => { setActiveQRType('objectId'); setTimeout(() => downloadQRPdf(true), 100); }}
                      className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaFilePdf className="w-4 h-4" />
                      <span>PDF + Name</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Part Number QR Code */}
              {qrCodeUrls.partNumber && (
                <div className={`p-4 sm:p-6 rounded-xl border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <h4 className={`text-sm sm:text-md font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    Part Number QR Code
                  </h4>
                  <div className="flex flex-col items-center space-y-3">
                    <div className="bg-white p-3 sm:p-4 rounded-lg">
                      <img src={qrCodeUrls.partNumber} alt="Part Number QR Code" className="w-48 h-48 sm:w-64 sm:h-64" />
                    </div>
                    <p className={`text-xs sm:text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Part Number: {selectedItemForQR.partNumber}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button
                        onClick={() => handleDownloadQR('partNumber')}
                        className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaDownload className="w-4 h-4" /> 
                        <span>PNG</span>
                      </button>
                      <button
                        onClick={() => { setActiveQRType('partNumber'); setTimeout(() => downloadQRPdf(false), 100); }}
                        className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaFilePdf className="w-4 h-4" />
                        <span>PDF</span>
                      </button>
                      <button
                        onClick={() => { setActiveQRType('partNumber'); setTimeout(() => downloadQRPdf(true), 100); }}
                        className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaFilePdf className="w-4 h-4" />
                        <span>PDF + Name</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* QR Name Info */}
              {!qrName && (
                <p className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Using default name "DOMUS" for PDF with name (QR Name not configured for company)
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stock Item Details Modal Component
const StockItemDetailsModal = ({ stockItem, onClose, onEditStock, onEditImage, onStatusChange, statusChangeLoading, darkMode }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stockItem.name}</h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors ${
                darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Image and Basic Info */}
            <div>
              <div className="mb-6">
                {stockItem.image ? (
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}/stock-items/image/${stockItem._id}`}
                    alt={stockItem.name}
                    className={`w-full h-64 object-cover rounded-lg border ${darkMode ? 'border-gray-600' : ''}`}
                  />
                ) : (
                  <div className={`w-full h-64 rounded-lg border flex items-center justify-center ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100'}`}>
                    <FaImage className={`w-12 h-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                )}
                <button
                  onClick={() => onEditImage(stockItem)}
                  className={`mt-2 w-full py-2 px-4 rounded-lg transition-colors ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-600 text-white hover:bg-gray-700'}`}
                >
                  <FaImage className="w-4 h-4 inline mr-2" />
                  Update Image
                </button>
              </div>

              <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Basic Information</h3>
                <div className="space-y-2">
                  <DetailRow label="ID" value={stockItem.id} darkMode={darkMode} />
                  <DetailRow label="Part Number" value={stockItem.partNumber} darkMode={darkMode} />
                  <DetailRow label="Name" value={stockItem.name} darkMode={darkMode} />
                  <DetailRow label="Brand" value={stockItem.brand?.name} darkMode={darkMode} />
                  <DetailRow label="Category" value={stockItem.category?.name} darkMode={darkMode} />
                  <DetailRow label="Unit of Measure" value={stockItem.unitOfMeasure} darkMode={darkMode} />
                  <DetailRow label="Status" value={stockItem.status} darkMode={darkMode} />
                  <DetailRow label="Created At" value={(() => {
                    const date = new Date(stockItem.createdAt);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                  })()} darkMode={darkMode} />
                </div>
              </div>
            </div>

            {/* Right Column - Stock Information */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Stock Information</h3>
                <button
                  onClick={() => onStatusChange(stockItem)}
                  disabled={statusChangeLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    stockItem.status === 'active'
                      ? darkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-100 text-red-700 hover:bg-red-200'
                      : darkMode ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50' : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {statusChangeLoading ? 'Updating...' : (stockItem.status === 'active' ? 'Deactivate' : 'Activate')}
                </button>
              </div>

              <div className="space-y-4">
                {stockItem.stock && stockItem.stock.length > 0 ? (
                  stockItem.stock.map((stockEntry, index) => (
                    <div key={index} className={`border rounded-lg p-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {stockEntry.location?.locationName || stockEntry.location?.name || 'Unknown Location'}
                        </h4>
                        <button
                          onClick={() => onEditStock(stockItem, stockEntry)}
                          className={`text-sm flex items-center ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                        >
                          <FaEdit className="w-3 h-3 mr-1" />
                          Edit Stock
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Quantity:</span>
                          <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            {stockEntry.freshQuantity || 0}
                          </div>
                        </div>
                        <div>
                          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Damaged Quantity:</span>
                          <div className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                            {stockEntry.damagedQuantity || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    No stock information available
                  </div>
                )}
              </div>

              {/* Total Stock Summary */}
              <div className={`mt-6 rounded-lg p-4 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <h4 className={`font-medium mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-900'}`}>Total Stock Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={`${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Total Fresh:</span>
                    <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                      {stockItem.stock?.reduce((sum, s) => sum + (s.freshQuantity || 0), 0) || 0}
                    </div>
                  </div>
                  <div>
                    <span className={`${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Total Damaged:</span>
                    <div className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {stockItem.stock?.reduce((sum, s) => sum + (s.damagedQuantity || 0), 0) || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Ordered (Reserved by Sales Orders) */}
              {(stockItem.salesOrder?.freshQuantity > 0 || stockItem.salesOrder?.damagedQuantity > 0) && (
                <div className={`mt-4 rounded-xl p-4 ${
                  darkMode ? 'bg-purple-900/30 border border-purple-800/50' : 'bg-purple-50 border border-purple-100'
                }`}>
                  <h4 className={`font-medium mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-900'}`}>Sales Ordered</h4>
                  <div className="text-sm">
                    <span className={darkMode ? 'text-purple-400' : 'text-purple-700'}>Reserved by Pending Sales Orders:</span>
                    <div className="grid grid-cols-2 gap-4 mt-1">
                      <div>
                        <span className={darkMode ? 'text-purple-400' : 'text-purple-700'}>Fresh:</span>
                        <div className={`font-medium ${darkMode ? 'text-purple-200' : 'text-purple-900'}`}>
                          {parseFloat((stockItem.salesOrder?.freshQuantity || 0).toFixed(4))}
                        </div>
                      </div>
                      <div>
                        <span className={darkMode ? 'text-purple-400' : 'text-purple-700'}>Damaged:</span>
                        <div className={`font-medium ${darkMode ? 'text-purple-200' : 'text-purple-900'}`}>
                          {parseFloat((stockItem.salesOrder?.damagedQuantity || 0).toFixed(4))}
                        </div>
                      </div>
                    </div>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      Quantities reserved by sales orders not yet delivered.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for detail rows
const DetailRow = ({ label, value, className = '', darkMode = false }) => (
  <div className={`flex justify-between items-center py-2 border-b last:border-b-0 ${darkMode ? 'border-gray-600' : 'border-gray-100'}`}>
    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}:</span>
    <span className={`${darkMode ? 'text-white' : 'text-gray-900'} ${className}`}>{value || 'N/A'}</span>
  </div>
);

export default StockItemsStatus;